import React, { useEffect, useState } from "react";
import axiosInstance from "../axiosInstance";
import Sidebar from "../components/Sidebar";

// Chart.js + react-chartjs-2 imports
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  ArcElement,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Pie, Bar, Line } from "react-chartjs-2";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  ArcElement,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

import "./GodAdminDashboard.css";

function GodAdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch stats on mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const { data } = await axiosInstance.get("/admin/stats");
        setStats(data);
      } catch (err) {
        setError(err.message || "Failed to fetch stats");
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) return <div className="loading">Loading admin stats...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (!stats) return <div>No stats available.</div>;

  /**
   * Expected stats object structure:
   * {
   *   signups: { total: number, lastFive: [...] },
   *   surveys: { total: number },
   *   tokens: { total: number, totalCost: number },
   *   serviceStatus: { openAI: string, mongoDB: string },
   *   accountTypes: { ... },
   *   monthlySignups: [ { month: "YYYY-MM", count: number }, ... ],
   *   weeklyTokens: [ { week: "YYYY-WW", tokensUsed: number, cost: number }, ... ],
   *   mostActive: [ { name: string, surveys: number }, ... ]
   * }
   */
  const {
    signups = {},
    surveys = {},
    tokens = {},
    serviceStatus = {},
    accountTypes = {},
    monthlySignups = [],
    weeklyTokens = [],
    mostActive = [],
  } = stats;

  // --- Core Stats Data (for the grid) ---
  const coreStats = [
    { title: "Total Signups", value: signups.total ?? 0 },
    { title: "Total Surveys", value: surveys.total ?? 0 },
    { title: "Total Tokens", value: tokens.total ?? 0 },
    { title: "Total Cost", value: `$${(tokens.totalCost ?? 0).toFixed(4)}` },
  ];

  // --- Service Status ---
  const serviceStatusItems = Object.entries(serviceStatus).map(([srv, st]) => (
    <div key={srv} className="service-status-item">
      {srv}: <span className={`status ${st === "OK" ? "ok" : "warn"}`}>{st}</span>
    </div>
  ));

  // --- Account Types Pie Chart ---
  const accountTypeLabels = Object.keys(accountTypes);
  const accountTypeValues = Object.values(accountTypes);
  const accountTypesData = {
    labels: accountTypeLabels,
    datasets: [
      {
        label: "Account Types",
        data: accountTypeValues,
        backgroundColor: ["#8884d8", "#82ca9d", "#ffc658", "#d0ed57"],
      },
    ],
  };
  const pieOptions = {
    responsive: true,
    plugins: {
      legend: { position: "bottom" },
      title: { display: true, text: "Account Types" },
    },
  };

  // --- Monthly Signups Bar Chart ---
  const signupsBarData = {
    labels: monthlySignups.map((item) => item.month),
    datasets: [
      {
        label: "Signups",
        data: monthlySignups.map((item) => item.count),
        backgroundColor: "rgba(75,192,192,0.6)",
      },
    ],
  };
  const barOptions = {
    responsive: true,
    plugins: {
      legend: { position: "bottom" },
      title: { display: true, text: "Monthly Signups" },
    },
  };

  // --- Weekly Tokens & Cost Line Chart ---
  const tokensLineData = {
    labels: weeklyTokens.map((item) => item.week),
    datasets: [
      {
        label: "Tokens Used",
        data: weeklyTokens.map((item) => item.tokensUsed),
        borderColor: "#82ca9d",
        fill: false,
        tension: 0.1,
        yAxisID: "y1",
      },
      {
        label: "Cost ($)",
        data: weeklyTokens.map((item) =>
          parseFloat((item.cost ?? 0).toFixed(4))
        ),
        borderColor: "#8884d8",
        fill: false,
        tension: 0.1,
        yAxisID: "y2",
      },
    ],
  };

  const lineOptions = {
    responsive: true,
    plugins: {
      legend: { position: "bottom" },
      title: { display: true, text: "Weekly Token Usage & Cost" },
    },
    scales: {
      y1: {
        type: "linear",
        position: "left",
        beginAtZero: true,
        title: { display: true, text: "Tokens" },
      },
      y2: {
        type: "linear",
        position: "right",
        beginAtZero: true,
        title: { display: true, text: "Cost in USD ($)" },
        grid: { drawOnChartArea: false },
      },
    },
  };

  // --- Last 5 Signups Table ---
  const lastFiveSignups = signups.lastFive || [];

  // --- Most Active Users Bar Chart ---
  const activeLabels = mostActive.map((u) => u.name);
  const activeValues = mostActive.map((u) => u.surveys);
  const activeBarData = {
    labels: activeLabels,
    datasets: [
      {
        label: "Surveys Completed",
        data: activeValues,
        backgroundColor: "#8884d8",
      },
    ],
  };
  const activeBarOptions = {
    responsive: true,
    plugins: {
      legend: { position: "bottom" },
      title: { display: true, text: "Most Active Users" },
    },
    scales: {
      y: { beginAtZero: true },
    },
  };

  return (
    <div className="dashboard-container">
      <div className="sidebar-container">
        <Sidebar />
      </div>
      <div className="main-content">
        <h1 className="dashboard-title">Admin Dashboard</h1>

        {/* Core Stats Grid */}
        <div className="core-stats">
          {coreStats.map((stat, index) => (
            <div key={index} className="stat-item">
              <div className="stat-title">{stat.title}</div>
              <div className="stat-number">{stat.value}</div>
            </div>
          ))}
        </div>

        {/* Service Status */}
        <div className="service-status">
          {serviceStatusItems}
        </div>

        {/* Charts Row */}
        <div className="charts-row">
          <div className="chart-container">
            <h2 className="chart-title">Monthly Signups</h2>
            {signupsBarData.labels.length > 0 ? (
              <Bar data={signupsBarData} options={barOptions} />
            ) : (
              <p>No monthly signups data</p>
            )}
          </div>
          <div className="chart-container">
            <h2 className="chart-title">Weekly Token Usage & Cost</h2>
            {weeklyTokens.length > 0 ? (
              <Line data={tokensLineData} options={lineOptions} />
            ) : (
              <p>No weekly token data</p>
            )}
          </div>
        </div>

        {/* Bottom Row: Last 5 Signups & Most Active Users */}
        <div className="bottom-row">
          <div className="table-container">
            <h2 className="table-title">Last 5 Signups</h2>
            {lastFiveSignups.length > 0 ? (
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {lastFiveSignups.map((su, idx) => (
                    <tr key={idx}>
                      <td>{su.name}</td>
                      <td>{su.email}</td>
                      <td>{new Date(su.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No recent signups.</p>
            )}
          </div>
          <div className="chart-container">
            <h2 className="chart-title">Most Active Users</h2>
            {activeLabels.length > 0 ? (
              <Bar data={activeBarData} options={activeBarOptions} />
            ) : (
              <p>No active user data</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default GodAdminDashboard;
